import { action, computed, makeObservable } from 'mobx';
import { Metadata } from '@clipr/lib';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { Maybe } from '../../core';
import { AuthContextType } from '../auth';
import { WidgetParams, WidgetParamsSchema, WidgetRouteParams } from './widgetParams';
import { readParamsFromQueryString } from '../../entities/params/paramsUtils';
import { ClientMode } from '../../kernel/kernelSchema';
import { RouteContext } from '../../routes/routeContext';
import { WidgetAuthMode } from './widgetSchema';
import { Theme, View } from '../ui/utils';
import { INIT_DEBUGGER, TRACE } from '../../core/debug/debugMacros';

export class WidgetService
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this, {
      isWidgetMode: computed,
      showProfileCard: computed,
      queryString: computed,
      queryParams: computed,
      widgetRouteParams: computed,
      widgetParams: computed,
      requestedWidgetParams: computed,
      teamId: computed,
      jobId: computed,
      metadataFilter: computed,
      tagsFilter: computed,
      authConnection: computed,
      authMode: computed,
      theme: computed,
      view: computed,
      logo: computed,
      allowShare: computed,
      allowDownload: computed,
      showHelp: computed,
      disableReactions: computed,
      disableComments: computed,
      disableTranscript: computed,
      disableIndex: computed,
      customRedirectUrl: computed,
      showTeamName: computed,
      showSourceBadge: computed,
      showProfile: computed,
      showTopicTags: computed,
      init: action,
      activateWidgetMode: action,
    });

    INIT_DEBUGGER(this, { color: 'deepgreen' });
    TRACE(this, `constructor()`);
  }

  readonly nodeType = 'WidgetService';

  get isInIFrame() {
    try {
      return (window !== window.parent);
    } catch (_err) { }
    return false;
  }

  get isWidgetMode() {
    return this.store.kernel.clientMode === ClientMode.Widget;
  }

  get showProfileCard() {
    // we could write a big `if` for the logic below, but why sacrifice readability?
    // it's also similar to the logic found in the WidgetProfileCard itself
    const { context } = this.store.authService;
    if (!context || !this.isWidgetMode)
      return false;

    switch (context.type) {
      case AuthContextType.Authenticated:
        return true;
      case AuthContextType.Anonymous:
        return context.allowActions;
    }

    return false;
  }

  get queryString(): string | null {
    return this.store.routingService.pageSearch;
  }

  get queryParams(): URLSearchParams | null {
    return this.store.routingService.pageSearchParams;
  }

  get widgetRouteParams(): WidgetRouteParams | null {
    const { routingService } = this.store;
    return routingService.firstWidgetRoute?.params ?? null;
  }

  /**
   * Returns the parsed widget params or the default value if it is defined.
   * If you want a list of parsed widget params without defaults, use `requestedWidgetParams`.
   */
  get widgetParams(): WidgetParams {
    return readParamsFromQueryString(WidgetParamsSchema, this.queryString ?? '', true);
  }

  /**
   * Returns the parsed widget params as requested by the client, without defaults.
   * If you want a list of parsed widget params including default values, use `widgetParams`.
   */
  get requestedWidgetParams(): WidgetParams {
    return readParamsFromQueryString(WidgetParamsSchema, this.queryString ?? '', false);
  }

  get teamId(): string | null {
    return this.widgetRouteParams?.teamId ?? this.widgetParams?.teamId ?? null;
  }

  get jobId(): string | null {
    return this.widgetRouteParams?.jobId ?? null;
  }

  // TODO: filters should not apply globally to all widgets — move to per-widget scope when usage is better understood
  get metadataFilter(): Partial<Metadata> | null {
    return this.widgetParams?.meta ?? null;
  }

  get tagsFilter(): string[] | null {
    return this.widgetParams?.tags ?? null;
  }

  get authConnection(): string | null {
    return this.widgetParams?.authConnection ?? null;
  }

  get authMode(): WidgetAuthMode | null {
    return this.widgetParams?.authMode ?? null;
  }

  get theme(): Theme | null {
    return this.widgetParams?.theme ?? null;
  }

  get view(): View | null {
    return this.widgetParams?.view ?? null;
  }

  get logo(): string | null {
    return this.widgetParams?.logo ?? null;
  }

  get allowShare(): boolean | null {
    return this.widgetParams?.allowShare ?? null;
  }

  get allowDownload(): boolean | null {
    return this.widgetParams?.allowDownload ?? null;
  }

  get showHelp(): boolean | null {
    return this.widgetParams?.showHelp ?? null;
  }

  get disableReactions(): boolean | null {
    return this.widgetParams?.disableReactions ?? null;
  }

  get disableComments(): boolean | null {
    return this.widgetParams?.disableComments ?? null;
  }

  get disableTranscript(): boolean | null {
    return this.widgetParams?.disableTranscript ?? null;
  }

  get disableIndex(): boolean | null {
    return this.widgetParams?.disableIndex ?? null;
  }

  get customRedirectUrl(): string | null {
    return this.widgetParams?.customRedirect ?? null;
  }

  get showTeamName(): boolean | null {
    return this.widgetParams?.showTeamName ?? null;
  }

  get showSourceBadge(): boolean | null {
    return this.widgetParams?.showSourceBadge ?? null;
  }

  get showProfile(): boolean | null {
    return this.widgetParams?.showProfile ?? null;
  }

  get showTopicTags(): boolean | null {
    return this.widgetParams?.showTopicTags ?? null;
  }

  init() {
    if (this.isInIFrame)
      this.activateWidgetMode();

    TRACE(this, `Initialized widget service with state: \n`, {
      widgetParams: this.widgetParams,
      requestedWidgetParams: this.requestedWidgetParams,
      widgetRouteParams: this.widgetRouteParams,
      teamId: this.teamId,
      jobId: this.jobId
    });
  }

  activateWidgetMode() {

    if (this.isWidgetMode)
      return;

    this.store.kernel.setWidgetMode();

    // once the widget mode is entered for the first time we suppress the GTM buttons once and for all
    // (we won't restore them once exiting the widget mode)
    const { ui } = this.store;
    ui.suppressFeedbackButton();
    ui.suppressHelpButton();

    const { theme, view, logo } = this;
    if (theme)
      ui.setTheme(theme);
    else
      ui.setTheme(Theme.Default);

    if (view)
      ui.setView(view);
    if (logo)
      ui.setLogo(this.logo);
  }

  handleWidgetRoute(_routeContext: RouteContext) {
    this.activateWidgetMode();
  }

  getIsWidgetMode(val?: Maybe<boolean>) {
    if (typeof val === 'boolean')
      return val;
    return this.isWidgetMode;
  }
}